import React, { useState } from 'react';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import type { CompanyRegistryCheckResult } from '@ballerine/digital-footprint-schemas';

import { ctw } from '@/common';
import { CopyButton } from '@/components';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/atoms/Collapsible';
import {
  Briefcase,
  Building2,
  Calendar,
  ChevronDown,
  FileText,
  Globe,
  Hash,
  MapPin,
  User,
  Users,
} from '../../../../../common/icons/lucide-adapter';

dayjs.extend(advancedFormat);

const VENDOR_DISPLAY_NAMES: Record<string, string> = {
  'open-corporates': 'OpenCorporates',
} as const;

type RegistryDataProps = {
  registryData?: CompanyRegistryCheckResult | null;
  className?: string;
};

const DataRow: React.FC<{
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  value: React.ReactNode;
  copyValue?: string;
}> = ({ label, Icon, value, copyValue }) => {
  if (!value) return null;

  return (
    <div className="mb-4 grid grid-cols-2 items-center gap-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 flex-shrink-0 text-zinc-500" />
        <p className="text-sm text-slate-400">{label}</p>
      </div>
      <div className="flex items-center gap-1.5">
        <div className="text-sm text-gray-900">{value}</div>
        {copyValue && <CopyButton textToCopy={copyValue} />}
      </div>
    </div>
  );
};

const ArrayDataRow: React.FC<{
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  items: Array<{ label: string; value: string }>;
}> = ({ label, Icon, items }) => {
  if (!items || items.length === 0) return null;

  return (
    <div className="mb-4 grid grid-cols-2 items-center gap-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 flex-shrink-0 text-zinc-500" />
        <p className="text-sm text-slate-400">{label}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {items.map((item, index) => (
          <div key={index} className="flex items-center gap-1.5 text-sm text-gray-900">
            <span>{item.label}</span>
            <CopyButton textToCopy={item.value} />
            {index < items.length - 1 && <span className="text-slate-400">,</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

type Officer = NonNullable<CompanyRegistryCheckResult['data']>['officers'][number];

const OfficerCard: React.FC<{ officer: Officer }> = ({ officer }) => {
  const statusText = officer.isInactive
    ? 'Inactive'
    : officer.currentStatus
      ? officer.currentStatus
      : 'Active';
  const statusColor = officer.isInactive ? 'text-red-600' : 'text-green-600';

  const dateRange =
    officer.startDate || officer.endDate
      ? `${officer.startDate ?? 'Unknown'} - ${officer.endDate ?? 'Present'}`
      : null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 flex-shrink-0 text-zinc-500" />
          <span className="text-sm font-medium text-gray-900">{officer.name}</span>
          <CopyButton textToCopy={officer.name} />
        </div>
        <span className={ctw('text-xs font-medium', statusColor)}>{statusText}</span>
      </div>

      {officer.position && (
        <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
          <Briefcase className="h-3 w-3 text-zinc-400" />
          <span>{officer.position}</span>
        </div>
      )}

      {officer.occupation && (
        <div className="mt-1 text-xs text-slate-500">Occupation: {officer.occupation}</div>
      )}

      {dateRange && (
        <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
          <Calendar className="h-3 w-3 text-zinc-400" />
          <span>{dateRange}</span>
        </div>
      )}

      {officer.address && (
        <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
          <MapPin className="h-3 w-3 text-zinc-400" />
          <span>{officer.address}</span>
        </div>
      )}
    </div>
  );
};

const INITIAL_OFFICERS_LIMIT = 5;

const getExpandButtonText = (
  total: number,
  hiddenActiveCount: number,
  inactiveCount: number,
): string => {
  const parts: string[] = [];

  if (hiddenActiveCount > 0) {
    parts.push(`${hiddenActiveCount} active`);
  }

  if (inactiveCount > 0) {
    parts.push(`${inactiveCount} inactive`);
  }

  const suffix = parts.length > 0 ? ` (+${parts.join(', ')})` : '';

  return `Show all ${total} officers${suffix}`;
};

const OfficersSection: React.FC<{ officers: Officer[] }> = ({ officers }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!officers || officers.length === 0) return null;

  const activeOfficers = officers.filter(o => !o.isInactive);
  const inactiveOfficers = officers.filter(o => o.isInactive);

  const initialActiveOfficers = activeOfficers.slice(0, INITIAL_OFFICERS_LIMIT);
  const remainingActiveOfficers = activeOfficers.slice(INITIAL_OFFICERS_LIMIT);
  const hasHiddenOfficers = remainingActiveOfficers.length > 0 || inactiveOfficers.length > 0;

  return (
    <div className="mt-6">
      <div className="mb-3 flex items-center gap-2">
        <Users className="h-4 w-4 text-zinc-500" />
        <h3 className="text-sm font-medium text-slate-700">
          Officers & Directors ({officers.length})
        </h3>
      </div>

      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        {activeOfficers.length > 0 && (
          <div className="mb-4">
            <p className="mb-2 text-xs text-slate-500">Active ({activeOfficers.length})</p>
            <div className="grid gap-2">
              {initialActiveOfficers.map((officer, index) => (
                <OfficerCard key={`active-${index}`} officer={officer} />
              ))}
            </div>

            {remainingActiveOfficers.length > 0 && (
              <CollapsibleContent
                className={ctw(
                  'mt-2 grid gap-2',
                  'data-[state=open]:animate-in data-[state=closed]:animate-out',
                  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                  'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2',
                )}
              >
                {remainingActiveOfficers.map((officer, index) => (
                  <OfficerCard key={`active-remaining-${index}`} officer={officer} />
                ))}
              </CollapsibleContent>
            )}
          </div>
        )}

        {inactiveOfficers.length > 0 && (
          <CollapsibleContent
            className={ctw(
              'mb-4',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2',
            )}
          >
            <p className="mb-2 text-xs text-slate-500">Inactive ({inactiveOfficers.length})</p>
            <div className="grid gap-2">
              {inactiveOfficers.map((officer, index) => (
                <OfficerCard key={`inactive-${index}`} officer={officer} />
              ))}
            </div>
          </CollapsibleContent>
        )}

        {hasHiddenOfficers && (
          <CollapsibleTrigger className="flex items-center gap-1 text-sm text-blue-600 transition-colors hover:text-blue-800 hover:underline">
            {isOpen
              ? 'Show less'
              : getExpandButtonText(
                  officers.length,
                  remainingActiveOfficers.length,
                  inactiveOfficers.length,
                )}
            <ChevronDown className={ctw('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
          </CollapsibleTrigger>
        )}
      </Collapsible>
    </div>
  );
};

export const RegistryData: React.FC<RegistryDataProps> = ({ registryData, className }) => {
  if (!registryData || !registryData.success || !registryData.data) {
    const errorMessage = registryData?.error || 'Registry data unavailable';

    return (
      <div className={ctw('rounded-2xl border border-slate-200 bg-slate-50 p-4', className)}>
        <p className="text-sm text-slate-600">{errorMessage}</p>
      </div>
    );
  }

  const { data } = registryData;

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMM Do YYYY');
  };

  const vendorDisplayName = VENDOR_DISPLAY_NAMES[registryData.source] || registryData.source;

  return (
    <div className={className}>
      <div className="mb-6 flex items-center gap-2 text-sm text-slate-500">
        <span>
          Source:{' '}
          {data.openCorporatesUrl ? (
            <a
              href={data.openCorporatesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {vendorDisplayName}
            </a>
          ) : (
            vendorDisplayName
          )}
        </span>
        <span>|</span>
        <span>Fetched: {formatDate(registryData.retrievedAt)}</span>
        <span>|</span>
        <span>Last updated: {formatDate(data.source.retrievedAt)}</span>
      </div>
      {/* Basic Company Information */}
      <DataRow label="Company Name" Icon={Building2} value={data.name} copyValue={data.name} />

      <DataRow
        label="Company Number"
        Icon={Hash}
        value={data.companyNumber}
        copyValue={data.companyNumber}
      />

      {/* Identifiers */}
      {data.identifiers && data.identifiers.length > 0 && (
        <ArrayDataRow
          label="External Identifiers"
          Icon={Hash}
          items={data.identifiers.map(identifier => ({
            label: `${identifier.identifierSystemName}: ${identifier.identifierSystemCode}`,
            value: identifier.identifierSystemCode,
          }))}
        />
      )}

      <DataRow
        label="Jurisdiction"
        Icon={Globe}
        value={data.jurisdictionCode}
        copyValue={data.jurisdictionCode}
      />

      <DataRow
        label="Company Type"
        Icon={FileText}
        value={data.companyType}
        copyValue={data.companyType || undefined}
      />

      <DataRow
        label="Current Status"
        Icon={FileText}
        value={data.currentStatus || (data.isInactive ? 'Inactive' : 'Active')}
        copyValue={data.currentStatus || (data.isInactive ? 'Inactive' : 'Active')}
      />

      {/* Branch Information */}
      {data.isBranch && (
        <DataRow
          label="Branch Status"
          Icon={FileText}
          value={data.branchStatus}
          copyValue={data.branchStatus || undefined}
        />
      )}

      {/* Dates */}
      <DataRow
        label="Incorporation Date"
        Icon={Calendar}
        value={data.incorporationDate}
        copyValue={data.incorporationDate || undefined}
      />

      <DataRow
        label="Dissolution Date"
        Icon={Calendar}
        value={data.dissolutionDate}
        copyValue={data.dissolutionDate || undefined}
      />

      {/* Address */}
      <DataRow
        label="Registered Address"
        Icon={MapPin}
        value={
          data.registeredAddress
            ? data.registeredAddressInFull ||
              [
                data.registeredAddress.streetAddress,
                data.registeredAddress.locality,
                data.registeredAddress.region,
                data.registeredAddress.postalCode,
                data.registeredAddress.country,
              ]
                .filter(Boolean)
                .join(', ')
            : null
        }
        copyValue={
          data.registeredAddress
            ? data.registeredAddressInFull ||
              [
                data.registeredAddress.streetAddress,
                data.registeredAddress.locality,
                data.registeredAddress.region,
                data.registeredAddress.postalCode,
                data.registeredAddress.country,
              ]
                .filter(Boolean)
                .join(', ')
            : undefined
        }
      />

      {/* Agent */}
      <DataRow
        label="Agent Name"
        Icon={Users}
        value={data.agentName}
        copyValue={data.agentName || undefined}
      />

      {/* Number of Employees */}
      <DataRow
        label="Number of Employees"
        Icon={Users}
        value={data.numberOfEmployees?.toString()}
        copyValue={data.numberOfEmployees?.toString()}
      />

      {/* Industries */}
      {data.industries && data.industries.length > 0 && (
        <ArrayDataRow
          label="Industry Codes"
          Icon={Building2}
          items={data.industries.map(industry => ({
            label: `${industry.code}${industry.description ? ` - ${industry.description}` : ''} (${industry.codeSchemeName})`,
            value: `${industry.code} - ${industry.description || 'No description'}`,
          }))}
        />
      )}

      {/* Previous Names */}
      {data.previousNames && data.previousNames.length > 0 && (
        <ArrayDataRow
          label="Previous Names"
          Icon={FileText}
          items={data.previousNames.map(name => ({
            label: `${name.companyName}${name.startDate ? ` (${name.startDate} - ${name.endDate || 'present'})` : ''}`,
            value: name.companyName,
          }))}
        />
      )}

      {/* Alternative Names */}
      {data.alternativeNames && data.alternativeNames.length > 0 && (
        <ArrayDataRow
          label="Alternative Names"
          Icon={FileText}
          items={data.alternativeNames.map(name => ({
            label: `${name.companyName}${name.language ? ` (${name.language})` : ''}${name.type ? ` [${name.type}]` : ''}`,
            value: name.companyName,
          }))}
        />
      )}

      {/* Officers & Directors */}
      {data.officers && <OfficersSection officers={data.officers} />}
    </div>
  );
};
